import React, { useState } from 'react';
import DropZone from './DropZone';
import { CallNote } from './types';
import styles from './MemoBuilder.module.css';

interface Props {
  notes: CallNote[];
  onChange: (notes: CallNote[]) => void;
}

function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Pull a date out of names like "call-2026-10-14.txt" before falling back to the file's modified date.
function dateFromFile(file: File): string {
  const match = file.name.match(/(\d{4})[-_.](\d{2})[-_.](\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : toDateInput(new Date(file.lastModified));
}

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const CallNotes: React.FC<Props> = ({ notes, onChange }) => {
  const [pasteText, setPasteText] = useState('');
  const [pasteLabel, setPasteLabel] = useState('');
  const [pasteDate, setPasteDate] = useState(() => toDateInput(new Date()));
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleFiles = async (files: File[]) => {
    const txt = files.filter((f) => f.name.toLowerCase().endsWith('.txt'));
    setError(txt.length < files.length ? 'Only .txt files are supported; other files were skipped.' : '');
    const added = await Promise.all(
      txt.map(async (file): Promise<CallNote> => ({
        id: newId(),
        label: file.name.replace(/\.txt$/i, ''),
        date: dateFromFile(file),
        text: (await file.text()).trim(),
        source: 'file',
      })),
    );
    onChange([...notes, ...added]);
  };

  const addPasted = () => {
    if (!pasteText.trim()) return;
    onChange([
      ...notes,
      { id: newId(), label: pasteLabel.trim() || 'Partner call', date: pasteDate, text: pasteText.trim(), source: 'paste' },
    ]);
    setPasteText('');
    setPasteLabel('');
  };

  const update = (id: string, patch: Partial<CallNote>) =>
    onChange(notes.map((n) => (n.id === id ? { ...n, ...patch } : n)));

  const sorted = [...notes].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className={styles.stack}>
      <DropZone
        accept=".txt,text/plain"
        multiple
        title="Drop call notes or click to browse"
        hint="One or more .txt files"
        onFiles={handleFiles}
      />
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.pasteBox}>
        <textarea
          className={`${styles.input} ${styles.textarea}`}
          rows={4}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder="Or paste notes from a partner call..."
        />
        <div className={styles.pasteRow}>
          <input
            className={styles.input}
            value={pasteLabel}
            onChange={(e) => setPasteLabel(e.target.value)}
            placeholder="Label (e.g. Founder call)"
            aria-label="Note label"
          />
          <input
            className={styles.input}
            type="date"
            value={pasteDate}
            onChange={(e) => setPasteDate(e.target.value)}
            aria-label="Call date"
          />
          <button type="button" className={styles.primaryButton} onClick={addPasted} disabled={!pasteText.trim()}>
            Add
          </button>
        </div>
      </div>

      {sorted.length > 0 && (
        <ul className={styles.noteList}>
          {sorted.map((note) => (
            <li key={note.id} className={styles.noteItem}>
              <div className={styles.noteHeader}>
                <input
                  className={`${styles.input} ${styles.noteLabel}`}
                  value={note.label}
                  onChange={(e) => update(note.id, { label: e.target.value })}
                  aria-label="Note label"
                />
                <input
                  className={styles.input}
                  type="date"
                  value={note.date}
                  onChange={(e) => update(note.id, { date: e.target.value })}
                  aria-label="Call date"
                  required
                />
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => onChange(notes.filter((n) => n.id !== note.id))}
                  aria-label={`Remove ${note.label}`}
                  title="Remove"
                >
                  &times;
                </button>
              </div>
              <div className={styles.noteMeta}>
                <span className={styles.muted}>
                  {note.source === 'file' ? 'Uploaded' : 'Pasted'}, {note.text.split(/\s+/).filter(Boolean).length} words
                </span>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setExpanded(expanded === note.id ? null : note.id)}
                >
                  {expanded === note.id ? 'Hide' : 'View'}
                </button>
              </div>
              {expanded === note.id && (
                <textarea
                  className={`${styles.input} ${styles.textarea}`}
                  rows={8}
                  value={note.text}
                  onChange={(e) => update(note.id, { text: e.target.value })}
                  aria-label={`${note.label} text`}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CallNotes;
